import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { parseISO } from "date-fns";
import { ClipboardText, CircleNotch, WarningCircle, MagnifyingGlass, DownloadSimple, UsersThree } from "@phosphor-icons/react";
import { NavTabs } from "@/components/NavTabs";
import { DateField } from "@/components/DateField";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Feb 2026, user's explicit ask: "build a report ... show User create
// shipment code for the supplier add a date filter (Site, Supplier
// code, supplier name, Shipment code, Created by, Created on date).
// Report any one can see who have act as supplier rights" - every
// shipment created via the Act as Supplier page, newest first. Gated
// behind the SAME "act_as_supplier" permission as that page itself
// (see /api/admin/act-as-supplier/report in server.py), so anyone who
// can act as a supplier can also view this report.
// Feb 2026 follow-up, user's explicit ask ("add filter in this report
// (by date, by creter, by site, by supplier)") - Created By/Site are
// dropdowns built from the distinct values actually present (see
// /api/admin/act-as-supplier/report/filter-options), Supplier is a
// free-text search matching either the supplier code or name.
const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return parseISO(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

export default function ActAsSupplierReportPage() {
  const [fromDate, setFromDate] = useState(isoDaysAgo(30));
  const [toDate, setToDate] = useState(isoDaysAgo(0));
  const [creatorFilter, setCreatorFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [supplierSearch, setSupplierSearch] = useState("");
  // Feb 2026, testing_agent iteration_352 finding: every keystroke was
  // firing its own request (correctness was fine - the AbortController
  // below already cancels stale ones - but needlessly). Debounced 300ms,
  // same convention as other free-text search inputs in this app.
  const [debouncedSupplierSearch, setDebouncedSupplierSearch] = useState("");
  const [filterOptions, setFilterOptions] = useState({ created_by: [], site_ids: [] });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Nov 2026, user's explicit ask: "add a button for see only distinct
  // supplier (only supplier which shipment code created by the user
  // behalf of supplier)" - toggles the table from one row per shipment
  // to one row per distinct supplier (vendor_code) that has had at
  // least one on-behalf-of shipment within the current filters.
  const [distinctSuppliers, setDistinctSuppliers] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/act-as-supplier/report/filter-options`)
      .then(({ data }) => setFilterOptions(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSupplierSearch(supplierSearch), 300);
    return () => clearTimeout(timer);
  }, [supplierSearch]);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${API}/admin/act-as-supplier/report`, {
          signal: controller.signal,
          params: {
            date_from: fromDate,
            date_to: toDate,
            created_by: creatorFilter !== "all" ? creatorFilter : undefined,
            site_id: siteFilter !== "all" ? siteFilter : undefined,
            supplier_search: debouncedSupplierSearch.trim() || undefined,
          },
        });
        setRows(data.report || []);
      } catch (err) {
        if (axios.isCancel(err)) return;
        setError(err?.response?.data?.detail || "Could not load the report");
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [fromDate, toDate, creatorFilter, siteFilter, debouncedSupplierSearch]);

  const filtersActive = fromDate !== isoDaysAgo(30) || toDate !== isoDaysAgo(0) || creatorFilter !== "all" || siteFilter !== "all" || supplierSearch !== "";
  const clearAllFilters = () => {
    setFromDate(isoDaysAgo(30));
    setToDate(isoDaysAgo(0));
    setCreatorFilter("all");
    setSiteFilter("all");
    setSupplierSearch("");
  };

  // Rows are already sorted newest-first by the backend (created_at
  // desc) - so the FIRST shipment seen per vendor_code is already that
  // supplier's most recent one, no extra date comparison needed.
  const distinctRows = useMemo(() => {
    const byVendor = new Map();
    for (const r of rows) {
      const key = r.vendor_code || r.company_name || r.shipment_code;
      const existing = byVendor.get(key);
      if (existing) {
        existing.shipment_count += 1;
      } else {
        byVendor.set(key, { ...r, shipment_count: 1 });
      }
    }
    return Array.from(byVendor.values());
  }, [rows]);

  const displayRows = distinctSuppliers ? distinctRows : rows;

  const exportExcel = () => {
    const headerRows = [
      [`Act as Supplier Report - ${fromDate} to ${toDate}`],
      [`Exported: ${new Date().toLocaleString("en-IN")}`],
      [],
    ];
    const columns = distinctSuppliers
      ? ["Site", "Supplier Code", "Supplier Name", "No. of Shipments", "Last Created By", "Last Created On"]
      : ["Site", "Supplier Code", "Supplier Name", "Shipment Code", "Created By", "Created On"];
    const dataRows = displayRows.map((r) => distinctSuppliers
      ? [r.site_id || "-", r.vendor_code || "-", r.company_name || "-", r.shipment_count, r.created_by || "-", fmtDate(r.created_at)]
      : [r.site_id || "-", r.vendor_code || "-", r.company_name || "-", r.shipment_code, r.created_by || "-", fmtDate(r.created_at)]);
    const sheet = XLSX.utils.aoa_to_sheet([...headerRows, columns, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, distinctSuppliers ? "Distinct Suppliers" : "Shipments");
    XLSX.writeFile(workbook, `act_as_supplier_report_${distinctSuppliers ? "distinct_suppliers_" : ""}${new Date().toISOString().slice(0, 10)}.xlsx`);
  };


  return (
    <div className="min-h-screen bg-[#F2F4F7] font-sans" data-testid="act-as-supplier-report-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <ClipboardText size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Materials Hub</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Act as Supplier Report</span>
          </div>
        </div>
        <NavTabs />
      </header>

      <main className="max-w-[1400px] mx-auto px-3 sm:px-5 py-5">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardText size={22} className="text-[#0E7C86]" />
          <h1 className="text-xl font-heading font-bold text-[#101828]" data-testid="act-as-supplier-report-title">Act as Supplier Report</h1>
        </div>
        <p className="text-sm text-[#475467] mb-4">
          Every shipment code a staff member has created on behalf of a supplier via "Act as Supplier", with who created it and when.
        </p>

        <div className="flex items-center gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-1.5 rounded-sm border border-[#D0D5DD] bg-white px-2 h-9 w-fit" data-testid="act-as-supplier-report-date-filter">
            <DateField
              value={fromDate}
              onChange={setFromDate}
              disabledMatcher={(d) => d > parseISO(toDate)}
              testId="act-as-supplier-report-date-filter-from"
              className="!h-7 !mt-0 !w-[120px] !border-none !px-1 !text-[12px]"
            />
            <span className="text-[#98A2B3] text-xs">to</span>
            <DateField
              value={toDate}
              onChange={setToDate}
              disabledMatcher={(d) => d < parseISO(fromDate)}
              testId="act-as-supplier-report-date-filter-to"
              className="!h-7 !mt-0 !w-[120px] !border-none !px-1 !text-[12px]"
            />
          </div>

          <Select value={creatorFilter} onValueChange={setCreatorFilter}>
            <SelectTrigger className="h-9 w-48 text-[13px] rounded-sm border-[#D0D5DD] bg-white" data-testid="act-as-supplier-report-creator-filter">
              <SelectValue placeholder="Filter by Created By" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="act-as-supplier-report-creator-filter-all">All Creators</SelectItem>
              {filterOptions.created_by.map((c) => (
                <SelectItem key={c} value={c} data-testid={`act-as-supplier-report-creator-filter-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="h-9 w-36 text-[13px] rounded-sm border-[#D0D5DD] bg-white" data-testid="act-as-supplier-report-site-filter">
              <SelectValue placeholder="Filter by Site" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="act-as-supplier-report-site-filter-all">All Sites</SelectItem>
              {filterOptions.site_ids.map((s) => (
                <SelectItem key={s} value={s} data-testid={`act-as-supplier-report-site-filter-${s}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#475569]" />
            <Input
              placeholder="Search supplier code or name..."
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              className="h-9 pl-8 pr-2 w-64 text-[13px] rounded-sm border-[#D0D5DD] bg-white"
              data-testid="act-as-supplier-report-supplier-search"
            />
          </div>

          {filtersActive && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-[12px] font-semibold text-[#1E40AF] hover:underline"
              title="Reset every filter back to the default"
              data-testid="act-as-supplier-report-clear-filters"
            >
              Clear Filters
            </button>
          )}

          <div className="flex items-center gap-2 ml-auto">
            <Button
              type="button"
              variant={distinctSuppliers ? "default" : "outline"}
              className={`h-9 gap-1.5 text-[13px] ${distinctSuppliers ? "bg-[#0E7C86] hover:bg-[#0B6B74]" : ""}`}
              onClick={() => setDistinctSuppliers((v) => !v)}
              title="Show one row per distinct supplier instead of one row per shipment"
              data-testid="act-as-supplier-report-distinct-toggle"
            >
              <UsersThree size={15} weight="bold" />
              {distinctSuppliers ? "Showing Distinct Suppliers" : "Distinct Suppliers Only"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-9 gap-1.5 text-[13px]"
              onClick={exportExcel}
              disabled={displayRows.length === 0}
              data-testid="act-as-supplier-report-export-excel-button"
            >
              <DownloadSimple size={15} weight="bold" />
              Export Excel
            </Button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-[#475467] text-sm" data-testid="act-as-supplier-report-loading">
            <CircleNotch size={16} className="animate-spin" /> Loading...
          </div>
        )}
        {error && (
          <div className="text-sm text-[#B91C1C] bg-[#E02424]/5 border border-[#E02424]/30 rounded-sm px-3 py-2" data-testid="act-as-supplier-report-error">
            <WarningCircle size={14} className="inline mr-1" /> {error}
          </div>
        )}
        {!loading && !error && displayRows.length === 0 && (
          <div className="text-sm text-[#475467] bg-white border border-[#D0D5DD] rounded-sm px-3 py-6 text-center" data-testid="act-as-supplier-report-empty">
            {distinctSuppliers ? "No distinct suppliers found matching these filters." : "No shipments were created on behalf of a supplier matching these filters."}
          </div>
        )}

        {!loading && !error && displayRows.length > 0 && (
          <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-x-auto" data-testid="act-as-supplier-report-table">
            <table className="border-collapse w-full text-xs">
              <thead className="bg-[#EAECF0] text-[#344054] text-xs font-bold font-heading tracking-wide">
                <tr>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Site</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Supplier Code</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Supplier Name</th>
                  {distinctSuppliers ? (
                    <th className="border border-[#D0D5DD] p-1.5 text-left">No. of Shipments</th>
                  ) : (
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Shipment Code</th>
                  )}
                  <th className="border border-[#D0D5DD] p-1.5 text-left">{distinctSuppliers ? "Last Created By" : "Created By"}</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">{distinctSuppliers ? "Last Created On" : "Created On"}</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((r, i) => (
                  <tr key={`${r.vendor_code || r.shipment_code}-${i}`} className="bg-white odd:bg-[#F9FAFB]" data-testid={distinctSuppliers ? `act-as-supplier-report-distinct-row-${r.vendor_code || i}` : `act-as-supplier-report-row-${r.shipment_code}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-data">{r.site_id || "-"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-data">{r.vendor_code || "-"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.company_name || "-"}</td>
                    {distinctSuppliers ? (
                      <td className="border border-[#D0D5DD] px-2 py-1.5 font-data font-semibold text-[#101828]">{r.shipment_count}</td>
                    ) : (
                      <td className="border border-[#D0D5DD] px-2 py-1.5 font-data font-semibold text-[#004B87]">{r.shipment_code}</td>
                    )}
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.created_by || "-"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap font-data">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

